import React, { useState, useEffect, useRef } from "react";
import "../../css/popup.css";
import { useAuth, signedFetch } from "../context/AuthContext";
import { useBookings } from "../context/BookingContext";

const getTodayString = () => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

function CustomLuxurySelect({ value, options, onChange, iconClass, dropUp = true }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="custom-luxury-select-container" ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        className={`custom-luxury-select-btn ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: "100%",
          padding: "13px 18px 13px 48px",
          background: "#0B1D37",
          border: isOpen ? "1.5px solid #FEA116" : "1.5px solid rgba(212, 175, 55, 0.45)",
          borderRadius: "14px",
          color: "#FEA116",
          fontWeight: "600",
          fontSize: "0.92rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: isOpen ? "0 0 16px rgba(254, 161, 22, 0.4), inset 0 2px 4px rgba(5, 14, 26, 0.3)" : "inset 0 2px 4px rgba(5, 14, 26, 0.25)",
          transition: "all 0.25s ease",
          cursor: "pointer",
          textAlign: "left"
        }}
      >
        {iconClass && (
          <i
            className={`${iconClass} unique-field-icon`}
            style={{
              position: "absolute",
              left: "18px",
              color: isOpen ? "#FEA116" : "rgba(212, 175, 55, 0.8)",
              fontSize: "1rem",
              transition: "color 0.25s ease"
            }}
          />
        )}
        <span style={{ color: "#FEA116", fontFamily: "'Poppins', sans-serif" }}>{value || options[0]}</span>
        <i
          className="fas fa-chevron-up"
          style={{
            fontSize: "0.8rem",
            color: "#FEA116",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.25s ease"
          }}
        />
      </button>

      {isOpen && (
        <div
          className="custom-luxury-select-dropdown shadow-lg"
          style={{
            position: "absolute",
            bottom: dropUp ? "calc(100% + 6px)" : "auto",
            top: dropUp ? "auto" : "calc(100% + 6px)",
            left: 0,
            width: "100%",
            background: "#07162C",
            border: "1.5px solid #FEA116",
            borderRadius: "14px",
            boxShadow: "0 -12px 36px rgba(0, 0, 0, 0.85), 0 0 15px rgba(254, 161, 22, 0.2)",
            zIndex: 99999,
            overflow: "hidden",
            padding: "6px",
            animation: "fadeIn 0.2s ease"
          }}
        >
          {options.map((opt, idx) => {
            const isSelected = opt === value;
            return (
              <div
                key={idx}
                onClick={() => {
                  onChange(opt);
                  setIsOpen(false);
                }}
                className="custom-luxury-option-item"
                style={{
                  padding: "11px 16px",
                  borderRadius: "10px",
                  color: isSelected ? "#FEA116" : "#FAF6F0",
                  background: isSelected ? "rgba(254, 161, 22, 0.22)" : "transparent",
                  fontWeight: isSelected ? "700" : "500",
                  fontSize: "0.92rem",
                  fontFamily: "'Poppins', sans-serif",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  transition: "all 0.18s ease"
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = "rgba(254, 161, 22, 0.14)";
                    e.currentTarget.style.color = "#FEA116";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "#FAF6F0";
                  }
                }}
              >
                <span>{opt}</span>
                {isSelected && <i className="fas fa-check-circle" style={{ color: "#FEA116", fontSize: "0.88rem" }} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Popup({ show, onClose, initialRoomType }) {
  const { user } = useAuth();
  const { rooms, validateCoupon } = useBookings();
  const defaultAdults = ["Adult 1", "Adult 2", "Adult 3", "Adult 4", "Adult 5+ (Luxury Group)"];
  const defaultChildren = ["Child 0", "Child 1", "Child 2", "Child 3", "Child 4+"];

  const adultOptionsList = (() => {
    try {
      const stored = localStorage.getItem("hk_popup_adult_options");
      return stored ? JSON.parse(stored) : defaultAdults;
    } catch (e) { return defaultAdults; }
  })();

  const childrenOptionsList = (() => {
    try {
      const stored = localStorage.getItem("hk_popup_children_options");
      return stored ? JSON.parse(stored) : defaultChildren;
    } catch (e) { return defaultChildren; }
  })();

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    number: "",
    checkInDate: "",
    checkInTime: "12:00",
    checkOutDate: "",
    checkOutTime: "11:00",
    adults: adultOptionsList[0] || "Adult 1",
    children: childrenOptionsList[0] || "Child 0",
    roomType: initialRoomType || "Standard Room",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [draftBookingId, setDraftBookingId] = useState(null);

  const [step, setStep] = useState(1); // 🔹 step 1 → 3

  // Coupon states & helpers
  const [couponCodeInput, setCouponCodeInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

  const getRoomPrice = () => {
    const room = rooms.find((r) => r.roomType === formData.roomType);
    if (room) {
      const rawPrice = parseFloat(room.pricePerNight || 3999);
      const discPct = parseFloat(room.discountPercent || 0);
      if (discPct > 0 && discPct <= 100) {
        return Math.round(rawPrice * (1 - discPct / 100));
      }
      return rawPrice;
    }
    if (formData.roomType === "Deluxe Room") return 4999;
    if (formData.roomType === "Super Deluxe") return 7999;
    return 3999;
  };

  const calculateNights = () => {
    if (!formData.checkInDate || !formData.checkOutDate) return 1;
    const checkInVal = new Date(formData.checkInDate);
    const checkOutVal = new Date(formData.checkOutDate);
    const diffTime = Math.abs(checkOutVal - checkInVal);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) || 1;
    return diffDays;
  };

  const handleApplyCoupon = async () => {
    if (!couponCodeInput) return;
    setCouponError("");
    setIsValidatingCoupon(true);

    const pricePerNight = getRoomPrice();
    const nights = calculateNights();
    const subtotal = pricePerNight * nights;

    const res = await validateCoupon(couponCodeInput.toUpperCase().trim(), subtotal);
    setIsValidatingCoupon(false);

    if (res.success) {
      setAppliedCoupon({
        code: res.coupon.code,
        discountAmount: res.coupon.discountAmount,
        discountValue: res.coupon.discountValue,
        discountType: res.coupon.discountType,
        minBookingAmount: res.coupon.minBookingAmount,
        finalCost: res.coupon.finalCost
      });
      setCouponError("");
    } else {
      setAppliedCoupon(null);
      setCouponError(res.error || "Invalid coupon code.");
    }
  };

  // Reset coupon if room type or dates change
  useEffect(() => {
    setAppliedCoupon(null);
    setCouponCodeInput("");
    setCouponError("");
  }, [formData.roomType, formData.checkInDate, formData.checkOutDate]);

  // Pre-fill user data if logged in
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        fullName: user.fullName || "",
        email: user.email || "",
        number: user.number || "",
      }));
    }
  }, [user]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      roomType: initialRoomType || "Standard Room",
    }));
  }, [initialRoomType]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setErrors((prev) => ({
      ...prev,
      [e.target.name]: "",
    }));
  };

  // 🔹 validate per step
  const validateStep = (currentStep) => {
    const newErrors = {};

    if (currentStep === 1) {
      if (!formData.fullName || formData.fullName.trim().length < 2) {
        newErrors.fullName = "Please enter a valid full name (min 2 characters).";
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!formData.email) {
        newErrors.email = "Email address is required.";
      } else if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = "Please enter a valid email address (e.g. name@example.com).";
      }

      const digitsOnly = formData.number ? formData.number.replace(/[^0-9]/g, "") : "";
      if (!formData.number) {
        newErrors.number = "Phone number is required.";
      } else if (digitsOnly.length < 10) {
        newErrors.number = "Please enter a valid 10-digit phone number.";
      }
    }

    if (currentStep === 2) {
      const todayStr = getTodayString();
      if (!formData.checkInDate) {
        newErrors.checkInDate = "Check-in date is required.";
      } else if (formData.checkInDate < todayStr) {
        newErrors.checkInDate = "Check-in date cannot be in the past.";
      }

      if (!formData.checkInTime) {
        newErrors.checkInTime = "Check-in time is required.";
      }

      if (!formData.checkOutDate) {
        newErrors.checkOutDate = "Check-out date is required.";
      } else if (formData.checkInDate && formData.checkOutDate < formData.checkInDate) {
        newErrors.checkOutDate = "Check-out date cannot be before check-in date.";
      }

      if (!formData.checkOutTime) {
        newErrors.checkOutTime = "Check-out time is required.";
      }

      if (formData.checkInDate && formData.checkInTime && formData.checkOutDate && formData.checkOutTime) {
        const checkInDateTime = new Date(`${formData.checkInDate}T${formData.checkInTime}`);
        const checkOutDateTime = new Date(`${formData.checkOutDate}T${formData.checkOutTime}`);
        const minTimeDiff = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
        
        if (isNaN(checkInDateTime.getTime()) || isNaN(checkOutDateTime.getTime())) {
          newErrors.checkOutDate = "Invalid stay date or time selected.";
        } else if (checkOutDateTime.getTime() - checkInDateTime.getTime() < minTimeDiff) {
          newErrors.checkOutDate = "Check-out must be at least 24 hours after check-in.";
        }
      }
    }

    if (currentStep === 3) {
      if (!formData.roomType)
        newErrors.roomType = "Please select a room type.";
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const validateAll = () => {
    const steps = [1, 2, 3];
    for (const s of steps) {
      const ok = validateStep(s);
      if (!ok) {
        setStep(s);
        return false;
      }
    }
    return true;
  };

  const getOrCreateActiveBookingId = () => {
    if (draftBookingId) return draftBookingId;
    const newId = `HK-${Date.now().toString().slice(-6)}`;
    setDraftBookingId(newId);
    return newId;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      const activeId = getOrCreateActiveBookingId(formData.email, formData.number);

      const pricePerNight = getRoomPrice();
      const nights = calculateNights();
      const subtotal = pricePerNight * nights;

      // ⚡ Progressive Background Lead Capture: Save/Update immediately to DB without showing any UI alerts
      const draftRecord = {
        bookingId: activeId,
        guestName: formData.fullName,
        guestEmail: formData.email,
        guestNumber: formData.number,
        roomType: formData.roomType || "Standard Room",
        checkIn: formData.checkInDate ? `${formData.checkInDate} (${formData.checkInTime || "12:00"})` : "Pending Dates",
        checkOut: formData.checkOutDate ? `${formData.checkOutDate} (${formData.checkOutTime || "11:00"})` : "Pending Dates",
        adults: formData.adults || "1 Adult",
        children: formData.children || "0 Children",
        totalCost: subtotal || 3999,
        status: "Pending",
        createdAt: new Date().toISOString()
      };

      // Save to client local bookings array (synced with Super Admin)
      try {
        const hkExisting = JSON.parse(localStorage.getItem("hk_bookings") || localStorage.getItem("bookings") || "[]");
        const idx = hkExisting.findIndex((b) => b.bookingId === activeId || b.id === activeId);
        if (idx >= 0) {
          hkExisting[idx] = { ...hkExisting[idx], ...draftRecord };
        } else {
          hkExisting.unshift(draftRecord);
        }
        localStorage.setItem("hk_bookings", JSON.stringify(hkExisting));
        localStorage.setItem("bookings", JSON.stringify(hkExisting));
        window.dispatchEvent(new Event("storage"));
      } catch (err) {}

      // Silently dispatch payload to server API asynchronously
      signedFetch({
        action: "createBooking",
        bookingId: activeId,
        fullName: formData.fullName,
        email: formData.email,
        number: formData.number,
        roomType: formData.roomType || "Standard Room",
        checkInDate: formData.checkInDate || "-",
        checkInTime: formData.checkInTime || "-",
        checkOutDate: formData.checkOutDate || "-",
        checkOutTime: formData.checkOutTime || "-",
        adults: formData.adults || "1 Adult",
        children: formData.children || "0 Children",
        totalCost: subtotal || 3999,
      }).catch(() => {});

      // Move smoothly to next step
      setStep((prev) => Math.min(prev + 1, 3));
    }
  };

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateAll()) return;

    setIsSubmitting(true);
    const activeId = getOrCreateActiveBookingId(formData.email, formData.number);
    const pricePerNight = getRoomPrice();
    const nights = calculateNights();
    const subtotal = pricePerNight * nights;

    const finalRecord = {
      bookingId: activeId,
      guestName: formData.fullName,
      guestEmail: formData.email,
      guestNumber: formData.number,
      roomType: formData.roomType,
      checkIn: `${formData.checkInDate} (${formData.checkInTime || "12:00"})`,
      checkOut: `${formData.checkOutDate} (${formData.checkOutTime || "11:00"})`,
      adults: formData.adults,
      children: formData.children,
      totalCost: appliedCoupon ? appliedCoupon.finalCost : subtotal,
      couponCode: appliedCoupon ? appliedCoupon.code : "",
      status: "Pending",
      createdAt: new Date().toISOString()
    };

    try {
      const hkExisting = JSON.parse(localStorage.getItem("hk_bookings") || localStorage.getItem("bookings") || "[]");
      const idx = hkExisting.findIndex((b) => b.bookingId === activeId || b.id === activeId);
      if (idx >= 0) {
        hkExisting[idx] = { ...hkExisting[idx], ...finalRecord };
      } else {
        hkExisting.unshift(finalRecord);
      }
      localStorage.setItem("hk_bookings", JSON.stringify(hkExisting));
      localStorage.setItem("bookings", JSON.stringify(hkExisting));
      window.dispatchEvent(new Event("storage"));
    } catch (err) {}

    signedFetch({
      action: "createBooking",
      bookingId: activeId,
      fullName: formData.fullName,
      email: formData.email,
      number: formData.number,
      roomType: formData.roomType,
      checkInDate: formData.checkInDate,
      checkInTime: formData.checkInTime,
      checkOutDate: formData.checkOutDate,
      checkOutTime: formData.checkOutTime,
      adults: formData.adults,
      children: formData.children,
      totalCost: appliedCoupon ? appliedCoupon.finalCost : subtotal,
      couponCode: appliedCoupon ? appliedCoupon.code : "",
    })
      .then((data) => {
        setIsSubmitting(false);
        setIsSubmitted(true);
      })
      .catch((error) => {
        setIsSubmitting(false);
        setIsSubmitted(true);
      });
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    if (show) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div
      className="unique-popup-container"
      role="dialog"
      aria-modal="true"
      aria-labelledby="booking-popup-title"
    >
      <div className="unique-popup-content">
        <button
          type="button"
          className="unique-close-btn"
          onClick={onClose}
          aria-label="Close booking modal"
        >
          ✕
        </button>

        {!isSubmitted ? (
          <>
            {/* 🔹 Stepper header */}
            <div className="unique-stepper">
              <div
                className={`unique-step ${step >= 1 ? "unique-step--active" : ""
                  } ${step === 1 ? "unique-step--current" : ""}`}
              >
                <span className="unique-step-index">1</span>
                <span className="unique-step-label">Guest</span>
              </div>
              <div
                className={`unique-step ${step >= 2 ? "unique-step--active" : ""
                  } ${step === 2 ? "unique-step--current" : ""}`}
              >
                <span className="unique-step-index">2</span>
                <span className="unique-step-label">Stay</span>
              </div>
              <div
                className={`unique-step ${step >= 3 ? "unique-step--active" : ""
                  } ${step === 3 ? "unique-step--current" : ""}`}
              >
                <span className="unique-step-index">3</span>
                <span className="unique-step-label">Room</span>
              </div>
            </div>

            <h2 className="unique-popup-title" id="booking-popup-title">Book Your Stay</h2>

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
              <div className="unique-popup-body">
                {/* STEP 1 – Guest details */}
                {step === 1 && (
                  <div className="unique-step-panel">
                    <div className="unique-form-group">
                      <label htmlFor="fullName" style={errors.fullName ? { color: "#ff4d4d", fontWeight: "bold" } : {}}>Full Name</label>
                      <div className="unique-input-icon-wrapper">
                        <i className="fas fa-user unique-field-icon" style={errors.fullName ? { color: "#ff4d4d" } : {}}></i>
                        <input
                          type="text"
                          id="fullName"
                          name="fullName"
                          placeholder="Enter guest full name"
                          value={formData.fullName}
                          onChange={handleChange}
                          style={errors.fullName ? { border: "1.5px solid #ff4d4d", boxShadow: "0 0 10px rgba(255, 77, 77, 0.4)" } : {}}
                        />
                      </div>
                      {errors.fullName && (
                        <span className="error-text fw-bold text-danger mt-1 d-block"><i className="fa fa-exclamation-circle me-1" />{errors.fullName}</span>
                      )}
                    </div>

                    <div className="unique-form-group">
                      <label htmlFor="email" style={errors.email ? { color: "#ff4d4d", fontWeight: "bold" } : {}}>Email Address</label>
                      <div className="unique-input-icon-wrapper">
                        <i className="fas fa-envelope unique-field-icon" style={errors.email ? { color: "#ff4d4d" } : {}}></i>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          placeholder="guest@example.com"
                          value={formData.email}
                          onChange={handleChange}
                          style={errors.email ? { border: "1.5px solid #ff4d4d", boxShadow: "0 0 10px rgba(255, 77, 77, 0.4)" } : {}}
                        />
                      </div>
                      {errors.email && (
                        <span className="error-text fw-bold text-danger mt-1 d-block"><i className="fa fa-exclamation-circle me-1" />{errors.email}</span>
                      )}
                    </div>

                    <div className="unique-form-group">
                      <label htmlFor="number" style={errors.number ? { color: "#ff4d4d", fontWeight: "bold" } : {}}>Phone Number</label>
                      <div className="unique-input-icon-wrapper">
                        <i className="fas fa-phone-alt unique-field-icon" style={errors.number ? { color: "#ff4d4d" } : {}}></i>
                        <input
                          type="tel"
                          id="number"
                          name="number"
                          placeholder="+91 9876543210"
                          value={formData.number}
                          onChange={handleChange}
                          style={errors.number ? { border: "1.5px solid #ff4d4d", boxShadow: "0 0 10px rgba(255, 77, 77, 0.4)" } : {}}
                        />
                      </div>
                      {errors.number && (
                        <span className="error-text fw-bold text-danger mt-1 d-block"><i className="fa fa-exclamation-circle me-1" />{errors.number}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* STEP 2 – Dates & times */}
                {step === 2 && (
                  <div className="unique-step-panel">
                    <div className="unique-form-row">
                      <div className="unique-form-group">
                        <label htmlFor="checkInDate">Check-In Date</label>
                        <div className="unique-input-icon-wrapper">
                          <i className="fas fa-calendar-alt unique-field-icon"></i>
                          <input
                            type="date"
                            id="checkInDate"
                            name="checkInDate"
                            value={formData.checkInDate}
                            onChange={handleChange}
                            min={getTodayString()}
                          />
                        </div>
                        {errors.checkInDate && (
                          <span className="error-text">
                            {errors.checkInDate}
                          </span>
                        )}
                      </div>

                      <div className="unique-form-group">
                        <label htmlFor="checkInTime">Check-In Time</label>
                        <div className="unique-input-icon-wrapper">
                          <i className="fas fa-clock unique-field-icon"></i>
                          <input
                            type="time"
                            id="checkInTime"
                            name="checkInTime"
                            value={formData.checkInTime}
                            onChange={handleChange}
                          />
                        </div>
                        {errors.checkInTime && (
                          <span className="error-text">
                            {errors.checkInTime}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="unique-form-row">
                      <div className="unique-form-group">
                        <label htmlFor="checkOutDate">Check-Out Date</label>
                        <div className="unique-input-icon-wrapper">
                          <i className="fas fa-calendar-alt unique-field-icon"></i>
                          <input
                            type="date"
                            id="checkOutDate"
                            name="checkOutDate"
                            value={formData.checkOutDate}
                            onChange={handleChange}
                            min={formData.checkInDate || getTodayString()}
                          />
                        </div>
                        {errors.checkOutDate && (
                          <span className="error-text">
                            {errors.checkOutDate}
                          </span>
                        )}
                      </div>

                      <div className="unique-form-group">
                        <label htmlFor="checkOutTime">Check-Out Time</label>
                        <div className="unique-input-icon-wrapper">
                          <i className="fas fa-clock unique-field-icon"></i>
                          <input
                            type="time"
                            id="checkOutTime"
                            name="checkOutTime"
                            value={formData.checkOutTime}
                            onChange={handleChange}
                          />
                        </div>
                        {errors.checkOutTime && (
                          <span className="error-text">
                            {errors.checkOutTime}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="unique-form-row">
                      <div className="unique-form-group">
                        <label htmlFor="adults">Adults</label>
                        <CustomLuxurySelect
                          value={formData.adults}
                          options={adultOptionsList}
                          onChange={(val) => setFormData((prev) => ({ ...prev, adults: val }))}
                          iconClass="fas fa-users"
                        />
                      </div>

                      <div className="unique-form-group">
                        <label htmlFor="children">Children</label>
                        <CustomLuxurySelect
                          value={formData.children}
                          options={childrenOptionsList}
                          onChange={(val) => setFormData((prev) => ({ ...prev, children: val }))}
                          iconClass="fas fa-child"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 3 – Room & review */}
                {step === 3 && (
                  <div className="unique-step-panel">
                    <div className="unique-form-group">
                      <label htmlFor="roomType">Select Room</label>
                      <CustomLuxurySelect
                        value={formData.roomType}
                        options={["Standard Room", "Deluxe Room", "Super Deluxe"]}
                        onChange={(val) => setFormData((prev) => ({ ...prev, roomType: val }))}
                        iconClass="fas fa-bed"
                        dropUp={false}
                      />
                      {errors.roomType && (
                        <span className="error-text">{errors.roomType}</span>
                      )}
                            <div className="unique-review-box">
                      <h4>Review Your Booking</h4>
                      <p>
                        <strong>Guest:</strong>{" "}
                        {formData.fullName || "-"}
                      </p>
                      <p>
                        <strong>Check-In:</strong>{" "}
                        {formData.checkInDate || "-"}{" "}
                        {formData.checkInTime || ""}
                      </p>
                      <p>
                        <strong>Check-Out:</strong>{" "}
                        {formData.checkOutDate || "-"}{" "}
                        {formData.checkOutTime || ""}
                      </p>
                      <p>
                        <strong>Guests:</strong> {formData.adults},{" "}
                        {formData.children}
                      </p>
                      <p>
                        <strong>Room Category:</strong> {formData.roomType}
                      </p>
                      <p style={{ borderTop: "1px dashed rgba(255, 255, 255, 0.15)", paddingTop: "8px", marginTop: "8px" }}>
                        <strong>Rate per Night:</strong> ₹{getRoomPrice().toLocaleString("en-IN")}
                      </p>
                      <p>
                        <strong>Total Stay:</strong> {calculateNights()} {calculateNights() === 1 ? "Night" : "Nights"}
                      </p>
                      <p>
                        <strong>Subtotal:</strong> ₹{(getRoomPrice() * calculateNights()).toLocaleString("en-IN")}
                      </p>
                      {appliedCoupon && (
                        <p style={{ color: "#2ecc71" }}>
                          <strong>Discount ({appliedCoupon.code}):</strong> -₹{appliedCoupon.discountAmount.toLocaleString("en-IN")}
                        </p>
                      )}
                      <p style={{ borderTop: "1.5px solid #FEA116", paddingTop: "8px", marginTop: "8px", fontSize: "1.1rem", color: "#FEA116" }}>
                        <strong>Grand Total:</strong> ₹{(appliedCoupon ? appliedCoupon.finalCost : (getRoomPrice() * calculateNights())).toLocaleString("en-IN")}
                      </p>
                    </div>

                    {/* Promo Coupon Form Field */}
                    <div className="coupon-promo-section mt-3 p-3 rounded" style={{ background: "rgba(255, 255, 255, 0.03)", border: "1px solid rgba(255, 255, 255, 0.08)", textTransform: "none" }}>
                      <label className="form-label text-light small fw-bold mb-2 d-block text-start">Promo / Coupon Code</label>
                      <div className="d-flex gap-2">
                        <input
                          type="text"
                          className="form-control unique-dojo-input m-0 text-uppercase"
                          style={{ flex: 1, backgroundColor: "#1e293b", color: "#f8fafc", border: "1px solid #475569", padding: "6px 12px" }}
                          placeholder="e.g. WELCOME10"
                          value={couponCodeInput}
                          onChange={(e) => {
                            setCouponCodeInput(e.target.value.toUpperCase());
                            setCouponError("");
                          }}
                          disabled={appliedCoupon}
                        />
                        {appliedCoupon ? (
                          <button
                            type="button"
                            className="btn btn-outline-danger px-3 py-1 btn-sm"
                            onClick={() => {
                              setAppliedCoupon(null);
                              setCouponCodeInput("");
                            }}
                          >
                            Remove
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-warning px-3 py-1 btn-sm"
                            style={{ whiteSpace: "nowrap", backgroundColor: "#FEA116", color: "#0F172B", fontWeight: "600", border: "none" }}
                            onClick={handleApplyCoupon}
                            disabled={isValidatingCoupon || !couponCodeInput}
                          >
                            {isValidatingCoupon ? "..." : "Apply"}
                          </button>
                        )}
                      </div>
                      {couponError && <div className="text-danger small mt-1 text-start">{couponError}</div>}
                      {appliedCoupon && (
                        <div className="text-success small mt-1 text-start">
                          <i className="fa fa-check-circle me-1" /> Coupon <strong>{appliedCoupon.code}</strong> applied! Saved ₹{appliedCoupon.discountAmount}.
                        </div>
                      )}
                    </div>                </div>
                  </div>
                )}
              </div>

              <div className="unique-popup-footer">
                <button
                  type="button"
                  className="unique-btn-secondary"
                  onClick={onClose}
                >
                  Cancel
                </button>

                {step > 1 && (
                  <button
                    type="button"
                    className="unique-btn-tertiary"
                    onClick={handleBack}
                  >
                    Back
                  </button>
                )}

                {step < 3 && (
                  <button
                    type="button"
                    className="unique-btn-primary"
                    onClick={handleNext}
                  >
                    Next
                  </button>
                )}

                {step === 3 && (
                  <button
                    type="submit"
                    className="unique-btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Confirm Booking..." : "Confirm Booking"}
                  </button>

                )}
              </div>
            </form>
          </>
        ) : (
          <div className="unique-thank-you-message">
            <div className="unique-success-icon" style={{
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #FEA116, #D4AF37)",
              color: "#050E1A",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "2rem",
              margin: "0 auto 20px",
              fontWeight: "bold",
              boxShadow: "0 8px 24px rgba(254, 161, 22, 0.4)"
            }}>✓</div>
            <h2>Enquiry Registered!</h2>
            <p style={{ color: "#FAF6F0", marginBottom: "20px" }}>
              Your reservation request has been successfully registered. We will contact you within 24 hours.
            </p>
            <div className="unique-success-summary" style={{
              background: "rgba(30, 62, 114, 0.2)",
              padding: "15px",
              borderRadius: "14px",
              border: "1px solid rgba(212, 175, 55, 0.35)",
              textAlign: "left",
              marginBottom: "20px",
              color: "#FAF6F0",
              fontSize: "0.9rem"
            }}>
              <p style={{ margin: "4px 0" }}><strong>Booking ID:</strong> <span style={{ color: "#FEA116", fontWeight: "bold" }}>{draftBookingId || "HK-117990"}</span></p>
              <p style={{ margin: "4px 0" }}><strong>Guest:</strong> {formData.fullName}</p>
              <p style={{ margin: "4px 0" }}><strong>Room Type:</strong> {formData.roomType}</p>
              <p style={{ margin: "4px 0" }}><strong>Check-In:</strong> {formData.checkInDate} ({formData.checkInTime})</p>
            </div>

            <button
              onClick={() => {
                const whatsappMsg = `Hello HariKunj Resort! I want to confirm my booking details:
Booking ID: ${draftBookingId || "HK-117990"}
Name: ${formData.fullName}
Email: ${formData.email}
Phone: ${formData.number}
Check-in: ${formData.checkInDate} ${formData.checkInTime}
Check-out: ${formData.checkOutDate} ${formData.checkOutTime}
Room: ${formData.roomType}
Guests: ${formData.adults}, ${formData.children}`;

                const whatsappUrl = `https://wa.me/919602588882?text=${encodeURIComponent(whatsappMsg)}`;
                window.open(whatsappUrl, "_blank");
              }}
              className="unique-btn-primary"
            >
              Confirm on WhatsApp
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
